import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { GET_ME_QUERY } from "@/features/auth/api/authDocuments";
import type { GetMeQueryResult } from "@/features/auth/api/authDocuments";
import type { User } from "@/features/auth/types";
import { apolloClient } from "@/shared/api/apolloClient";
import type { Permission } from "@/shared/permissions";

export const useUserStore = defineStore("user", () => {
  const user = ref<User | null>(null);
  const isInitialized = ref(false);
  const initError = ref<string | null>(null);
  const initLoading = ref(false);

  // ── Computed ────────────────────────────────────────────────────────────────

  const isAuthenticated = computed(() => !!user.value);

  const userInitials = computed(() => {
    if (!user.value) return "?";
    const name = user.value.name || user.value.email;
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  });

  const userDisplayName = computed(() => {
    if (!user.value) return "";
    return user.value.name || user.value.email;
  });

  const orgId = computed(() => user.value?.orgId ?? null);

  // ── Permission check ─────────────────────────────────────────────────────────
  // Scoped to user.orgId — only checks permissions for the user's current org.
  //
  // When multi-org switching is implemented, update this to use the active org ID
  // from a dedicated org-switcher store instead of user.orgId.
  const currentOrgPermissions = computed<readonly string[]>(() => {
    if (!user.value?.orgId) return [];
    const orgId = user.value.orgId;
    return user.value.orgPermissions.find(op => op.orgId === orgId)?.permissions ?? [];
  });

  const hasPermission = (permission: Permission): boolean =>
    currentOrgPermissions.value.includes(permission);

  // ── Actions ─────────────────────────────────────────────────────────────────

  /** Fetches /me — no-op if already loaded. Called by auth guard and login flow. */
  const initialize = async (): Promise<void> => {
    if (user.value || isInitialized.value) return;
    initError.value = null;
    initLoading.value = true;
    try {
      const { data } = await apolloClient.query<GetMeQueryResult>({
        query: GET_ME_QUERY,
        fetchPolicy: "network-only",
      });
      if (data?.me) user.value = data.me;
      isInitialized.value = true;
    }
    catch (err) {
      initError.value = err instanceof Error ? err.message : "Failed to load user";
      isInitialized.value = true;
    }
    finally {
      initLoading.value = false;
    }
  };

  const setUser = (newUser: User): void => {
    user.value = newUser;
    isInitialized.value = true;
  };

  const clearUser = (): void => {
    user.value = null;
    isInitialized.value = false;
    initError.value = null;
  };

  return {
    user,
    isInitialized,
    initLoading,
    initError,

    isAuthenticated,
    userInitials,
    userDisplayName,
    orgId,

    initialize,
    setUser,
    clearUser,
    hasPermission,
    currentOrgPermissions,
  };
});
